import { useEffect, useMemo, useState } from 'react';
import { Check, RotateCcw, Search } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn } from '@/shared/utils/utils';
import {
  usePermissionCatalog,
  useSaveUserPermissions,
  useUserPermissions,
} from '@/features/roles/hooks/usePermissionAdmin';
import type { HrUser } from '../types';

interface Props {
  user: HrUser | null;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export function UserPermissionsModal({ user, open, onClose, onSaved }: Props) {
  const catalog = usePermissionCatalog();
  const permissions = useUserPermissions(open ? user?.id ?? null : null);
  const save = useSaveUserPermissions();
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (permissions.data) setDraft(new Set(permissions.data.effective));
  }, [permissions.data]);

  const modules = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (catalog.data?.modules ?? []).map((module) => ({
      ...module,
      actions: module.actions.filter((action) => !term
        || module.label.toLowerCase().includes(term)
        || action.label.toLowerCase().includes(term)
        || action.key.includes(term)),
    })).filter((module) => module.actions.length);
  }, [catalog.data, search]);

  const toggle = (key: string) => setDraft((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const setModule = (keys: string[], mode: 'all' | 'none' | 'role') => setDraft((current) => {
    const next = new Set(current);
    const roleEffective = new Set(permissions.data?.roleEffective ?? []);
    for (const key of keys) {
      if (mode === 'all' || (mode === 'role' && roleEffective.has(key))) next.add(key);
      else next.delete(key);
    }
    return next;
  });

  const submit = () => {
    if (!user) return;
    save.mutate({ userId: user.id, keys: [...draft] }, {
      onSuccess: () => { onSaved?.(); onClose(); },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Manual authority — ${user?.display_name ?? ''}`}
      size="xl"
      footer={<>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={save.isPending} disabled={!user || permissions.data?.locked}>Save authority</Button>
      </>}
    >
      <div className="space-y-4">
        <p className="text-body-sm text-on-surface-variant">
          Choose the exact actions this user can perform. These switches override the selected role, so manual lead creation can be allowed while lead deletion stays denied.
        </p>
        <Input leftIcon={<Search className="h-4 w-4" />} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search module or action" />
        {catalog.isLoading || permissions.isLoading ? <div className="flex justify-center py-12"><Spinner /></div> : (
          <div className="space-y-3">
            {modules.map((module) => (
              <section key={module.module} className="rounded-card border border-outline p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-data-md font-semibold text-on-surface">{module.label}</h3>
                  <div className="flex items-center gap-2 text-[11px]">
                    <button type="button" className="text-primary hover:underline" onClick={() => setModule(module.actions.map((action) => action.key), 'all')}>Allow all</button>
                    <button type="button" className="text-danger-text hover:underline" onClick={() => setModule(module.actions.map((action) => action.key), 'none')}>Deny all</button>
                    <button type="button" className="flex items-center gap-1 text-on-surface-variant hover:underline" onClick={() => setModule(module.actions.map((action) => action.key), 'role')}>
                      <RotateCcw className="h-3 w-3" /> Role default
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {module.actions.map((action) => {
                    const checked = draft.has(action.key);
                    const inherited = permissions.data?.roleEffective.includes(action.key) ?? false;
                    return (
                      <button
                        type="button"
                        key={action.key}
                        onClick={() => toggle(action.key)}
                        className={cn('flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-body-sm', checked ? 'border-primary bg-primary-container text-on-primary-container' : 'border-outline text-on-surface-variant')}
                        title={`${action.key}${checked === inherited ? ' (inherited)' : ' (manual override)'}`}
                      >
                        {checked && <Check className="h-3.5 w-3.5" />}{action.label}
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
        {save.isError && <p className="text-body-sm text-danger-text">Could not save authority. Please try again.</p>}
      </div>
    </Modal>
  );
}
